import { useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Star, Film, Play, X, Award, Eye, Heart, Sparkles, ChevronLeft, ChevronRight, Home, Send } from 'lucide-react';
import { getMovieDetailsEnriched, type OMDBEnrichment } from '../api/tmdb';
import { getPosterUrl, getBackdropUrl, formatRuntime, calculateOurRating, getProfileUrl, getComingOnLabel } from '../utils/helpers';
import { queryKeys } from '../lib/queryKeys';
import { GENRES } from '../utils/constants';
import { useUserLibrary } from '../contexts/UserLibraryContext';
import { useAuth } from '../contexts/AuthContext';
import { useUserRating } from '../hooks/useRatings';
import { RatingModal } from './RatingModal';
import { RecommendModal } from './RecommendModal';
import { AddToListDropdown } from './AddToListDropdown';
import { ShareButton } from './ShareButton';
import { useToast } from './ui/Toast';
import { MovieDetailSkeleton } from './ui/Skeleton';
import { MovieCard } from './MovieCard';
import { JustWatchWidget } from './JustWatchWidget';
import type { Movie, CastMember, CrewMember, MediaItem, SimilarItem } from '../types/movie';

interface VideoResult {
  id: string;
  key: string;
  name: string;
  site: string;
  type: string;
}

// =============================================================================
// SIMILAR SHELF COMPONENT (For You shelf mechanics)
// =============================================================================

interface SimilarShelfProps {
  similar: SimilarItem[] | undefined;
  navigate: ReturnType<typeof useNavigate>;
}

function SimilarShelf({ similar, navigate }: SimilarShelfProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Drag-to-scroll: use refs for pointer values (avoids stale closures in handlers)
  const [isDragging, setIsDragging] = useState(false);
  const isTrackingRef = useRef(false);
  const isDraggingRef = useRef(false);
  const hasCapturedRef = useRef(false);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);
  const dragDistanceRef = useRef(0);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 320;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  // Pointer drag-to-scroll handlers
  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!scrollRef.current) return;

    // Ignore if pointerdown originates from interactive element
    const target = e.target as HTMLElement;
    if (target.closest('button, a, input, textarea, select, [role="button"], [role="menuitem"]')) {
      return;
    }

    isTrackingRef.current = true;
    isDraggingRef.current = false;
    hasCapturedRef.current = false;
    startXRef.current = e.clientX;
    scrollLeftRef.current = scrollRef.current.scrollLeft;
    dragDistanceRef.current = 0;
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isTrackingRef.current || !scrollRef.current) return;

    const dx = e.clientX - startXRef.current;
    dragDistanceRef.current = Math.abs(dx);

    // Only capture pointer and start dragging after threshold exceeded (3px)
    if (!hasCapturedRef.current && dragDistanceRef.current > 3) {
      try {
        scrollRef.current.setPointerCapture(e.pointerId);
        hasCapturedRef.current = true;
      } catch {
        // Pointer may have been released
      }
      isDraggingRef.current = true;
      setIsDragging(true);
    }

    if (isDraggingRef.current) {
      scrollRef.current.scrollLeft = scrollLeftRef.current - dx;
    }
  };

  const handlePointerEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    isTrackingRef.current = false;
    isDraggingRef.current = false;
    setIsDragging(false);

    if (hasCapturedRef.current && scrollRef.current) {
      try {
        scrollRef.current.releasePointerCapture(e.pointerId);
      } catch {
        // Browser quirks
      }
    }
    hasCapturedRef.current = false;
  };

  // Prevent navigation clicks after drag (threshold: 5px)
  const handleSimilarClick = (item: MediaItem) => {
    if (dragDistanceRef.current > 5) {
      return;
    }
    navigate(`/${item.media_type}/${item.id}`);
  };

  if (!similar || similar.length === 0) return null;

  // Map SimilarItem[] to MediaItem[] with real values from backend (cap to 20)
  const similarMediaItems: MediaItem[] = similar.slice(0, 20).map((sim) => ({
    id: sim.id,
    media_type: sim.media_type,
    title: sim.title || '',
    overview: '',
    poster_path: sim.poster_path,
    backdrop_path: null,
    release_date: sim.release_date || '',
    genre_ids: sim.genre_ids ?? [],
    vote_average: sim.vote_average ?? 0,
    vote_count: sim.vote_count ?? 0,
    popularity: sim.popularity ?? 0,
    original_language: sim.original_language ?? '',
    runtime: sim.runtime ?? undefined,
    imdb_rating: sim.imdb_rating ?? undefined,
    rt_rating: sim.rt_rating ?? undefined,
    filmpl_rating: sim.filmpl_rating ?? undefined,
    number_of_seasons: sim.number_of_seasons ?? undefined,
  }));

  return (
    <div className="mt-12 mb-10">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-white">You Might Enjoy</h2>
      </div>
      <div className="relative">
        {/* Left Arrow */}
        <button
          onClick={() => scroll('left')}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-[#0A0A0A]/80 hover:bg-black rounded-full flex items-center justify-center text-white opacity-30 hover:opacity-100 transition-opacity -ml-2"
        >
          <ChevronLeft size={24} />
        </button>

        {/* Movies Row with drag-to-scroll */}
        <div
          ref={scrollRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerEnd}
          onPointerLeave={handlePointerEnd}
          onPointerCancel={handlePointerEnd}
          className={`flex gap-4 overflow-x-auto scrollbar-hide pb-2 select-none ${
            isDragging ? 'cursor-grabbing' : 'cursor-grab'
          }`}
          style={{ touchAction: 'pan-y' }}
        >
          {similarMediaItems.map((item) => (
            <div key={`${item.media_type}-${item.id}`} className="flex-shrink-0 w-[160px]">
              <MovieCard
                movie={item}
                onClick={handleSimilarClick}
                showQuickActions={true}
                imagePriority="low"
              />
            </div>
          ))}
        </div>

        {/* Right Arrow */}
        <button
          onClick={() => scroll('right')}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 bg-[#0A0A0A]/80 hover:bg-black rounded-full flex items-center justify-center text-white opacity-30 hover:opacity-100 transition-opacity -mr-2"
        >
          <ChevronRight size={24} />
        </button>
      </div>
    </div>
  );
}

interface MovieDetails extends Movie {
  budget?: number;
  revenue?: number;
  tagline?: string;
  production_companies?: { id: number; name: string; logo_path: string | null }[];
  spoken_languages?: { english_name: string; iso_639_1: string }[];
  credits?: {
    cast: CastMember[];
    crew: CrewMember[];
  };
  videos?: {
    results: VideoResult[];
  };
  omdb?: OMDBEnrichment;
}

export function MovieDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const movieId = parseInt(id || '0', 10);
  const [showTrailer, setShowTrailer] = useState(false);

  // React Query for movie data - route-keyed to prevent stale renders
  const {
    data: movie,
    isLoading: loading,
    error: queryError,
  } = useQuery({
    queryKey: queryKeys.movie.detail(movieId),
    queryFn: () => getMovieDetailsEnriched(movieId) as Promise<MovieDetails>,
    enabled: movieId > 0,
  });

  const error = queryError ? 'Failed to load movie details' : null;

  // Watched tracking - uses optimistic UI from UserLibraryContext
  const { user } = useAuth();
  const { toast } = useToast();
  const { isWatched: checkIsWatched, toggleWatched: optimisticToggleWatched, isInFavorites, toggleFavorites } = useUserLibrary();
  const isLoggedIn = !!user;
  const isWatched = checkIsWatched(movieId, 'movie');
  const isFavorite = isInFavorites(movieId, 'movie');

  // Toggle watched with movie info for optimistic UI
  const toggleWatched = async () => {
    if (!movie) return;
    const wasWatched = isWatched;
    const year = movie.release_date ? parseInt(movie.release_date.slice(0, 4)) : undefined;
    try {
      const result = await optimisticToggleWatched(movieId, 'movie', movie.title || '', movie.poster_path || undefined, year);
      // result is null if superseded (user toggled again quickly) - ignore silently
      if (result) {
        toast.success(wasWatched ? 'Removed from watched' : 'Marked as watched');
      }
    } catch (err) {
      console.error('Error toggling watched:', err);
      toast.error('Something went wrong');
    }
  };

  // Toggle favorites with toast feedback
  const handleToggleFavorites = async () => {
    if (!movie) return;
    const wasFav = isFavorite;
    const year = movie.release_date ? parseInt(movie.release_date.slice(0, 4)) : undefined;
    try {
      const result = await toggleFavorites(movie.id, 'movie', movie.title, movie.poster_path || undefined, year);
      // result is null if superseded - ignore silently
      if (result) {
        toast.success(wasFav ? 'Removed from Favorites' : 'Added to Favorites');
      }
    } catch (err) {
      console.error('Error toggling favorites:', err);
      toast.error('Something went wrong');
    }
  };

  // Ratings - React Query (single source of truth)
  const { rating: userRating } = useUserRating(movieId, 'movie');
  const [isRatingModalOpen, setIsRatingModalOpen] = useState(false);
  const [showRecommendModal, setShowRecommendModal] = useState(false);

  // Skeleton while loading (no full-page block if cached)
  if (loading && !movie) {
    return <MovieDetailSkeleton />;
  }

  // Error state (only if no movie and not loading)
  if (!movie) {
    return (
      <div className="min-h-screen bg-[#0F1419] flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error || 'Movie not found'}</p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const directors = movie.credits?.crew.filter((c) => c.job === 'Director').slice(0, 2) || [];

  // Writers: match by department OR by specific writing-related jobs, then dedupe by id
  const WRITING_JOBS = ['Writer', 'Screenplay', 'Story', 'Original Film Writer', 'Characters'];
  const writersRaw = movie.credits?.crew.filter(
    (c) => c.department === 'Writing' || WRITING_JOBS.includes(c.job || '')
  ) || [];
  const seenWriterIds = new Set<number>();
  const writers = writersRaw.filter((w) => {
    if (seenWriterIds.has(w.id)) return false;
    seenWriterIds.add(w.id);
    return true;
  }).slice(0, 4);
  const cast = movie.credits?.cast.slice(0, 12) || [];
  const ourRating = calculateOurRating(movie.imdb_rating, movie.rt_rating);
  const year = movie.release_date?.split('-')[0];

  // Find trailer from videos - prioritize Trailer/Teaser, fall back to any YouTube video
  const youtubeVideos = movie.videos?.results.filter((v) => v.site === 'YouTube') || [];
  const trailerKey = youtubeVideos.find((v) => v.type === 'Trailer' || v.type === 'Teaser')?.key
    || youtubeVideos.find((v) => v.type === 'Clip')?.key
    || youtubeVideos[0]?.key;

  return (
    <div className="min-h-screen bg-[#111111] relative">
      {/* Hero Section with Backdrop - extends behind TopBar */}
      <div className="relative h-[400px] w-full overflow-hidden -mt-16">
        {/* Navigation buttons - top left */}
        <div className="absolute top-20 left-4 z-20 flex gap-2">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 px-3 py-2 bg-[#0F1419]/80 backdrop-blur-sm rounded-lg text-slate-300 hover:text-white transition-colors"
          >
            <ArrowLeft size={18} />
            <span className="text-sm font-medium">Back</span>
          </button>
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 px-3 py-2 bg-[#0F1419]/80 backdrop-blur-sm rounded-lg text-slate-300 hover:text-white transition-colors"
          >
            <Home size={18} />
            <span className="text-sm font-medium">Home</span>
          </button>
        </div>
        {movie.backdrop_path ? (
          <img
            src={getBackdropUrl(movie.backdrop_path, 'large')}
            alt={movie.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-slate-800 to-slate-900" />
        )}
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-[#111111]/60 to-transparent" />
      </div>

      {/* Trailer Modal */}
      {showTrailer && trailerKey && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#0A0A0A]/90 backdrop-blur-sm"
          onClick={() => setShowTrailer(false)}
        >
          <div className="relative w-full max-w-5xl mx-4 aspect-video">
            <button
              onClick={() => setShowTrailer(false)}
              className="absolute -top-12 right-0 p-2 text-white/70 hover:text-white transition-colors"
            >
              <X size={28} />
            </button>
            <iframe
              src={`https://www.youtube.com/embed/${trailerKey}?autoplay=1`}
              className="w-full h-full rounded-lg"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="px-6 pb-12 -mt-48 relative z-10">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Poster */}
          <div className="flex-shrink-0">
            <div className="w-[200px] aspect-[2/3] rounded-lg overflow-hidden shadow-xl bg-[#1A1F26]">
              {movie.poster_path ? (
                <img
                  src={getPosterUrl(movie.poster_path, 'large')}
                  alt={movie.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Film size={48} className="text-slate-600" />
                </div>
              )}
            </div>
          </div>

          {/* Details */}
          <div className="flex-1">
            {/* Title + Year */}
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-1">
              {movie.title}
              {year && <span className="text-slate-400 font-normal ml-2">({year})</span>}
            </h1>

            {/* Coming on date for unreleased movies */}
            {getComingOnLabel(movie.release_date) && (
              <p className="text-teal-400 text-sm font-medium mb-2">Coming on {getComingOnLabel(movie.release_date)}</p>
            )}

            {/* Tagline */}
            {movie.tagline && (
              <p className="text-slate-400 italic mb-4">"{movie.tagline}"</p>
            )}

            {/* Rating Badges - Horizontal pills */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {ourRating && (
                <div className="flex items-center gap-1.5 px-3 py-1 bg-purple-500 rounded-full">
                  <Sparkles size={14} className="text-purple-200" />
                  <span className="text-sm font-semibold text-white">{ourRating.toFixed(1)}</span>
                  <span className="text-xs font-medium text-purple-200 ml-0.5">FILMPLE</span>
                </div>
              )}
              {movie.imdb_rating && (
                <div className="flex items-center gap-1.5 px-3 py-1 bg-[#f5c518] rounded-full">
                  <Star size={14} className="text-yellow-600 fill-yellow-600" />
                  <span className="text-sm font-semibold text-black">{movie.imdb_rating.toFixed(1)}</span>
                  <span className="text-xs font-medium text-yellow-800 ml-0.5">IMDB</span>
                </div>
              )}
              {movie.rt_rating && (
                <div className="flex items-center gap-1.5 px-3 py-1 bg-[#fa320a] rounded-full">
                  <span className="text-sm">🍅</span>
                  <span className="text-sm font-semibold text-white">{movie.rt_rating}%</span>
                  <span className="text-xs font-medium text-red-200 ml-0.5">RT</span>
                </div>
              )}
            </div>

            {/* Metadata Row - Genres + Runtime + Rated separated by dots */}
            <div className="flex flex-wrap items-center gap-x-2 text-sm text-slate-400 mb-6">
              {movie.genre_ids && movie.genre_ids.length > 0 && (
                <>
                  <span>{movie.genre_ids.map(id => GENRES[id]).filter(Boolean).join(' • ')}</span>
                  <span className="text-slate-600">•</span>
                </>
              )}
              {movie.runtime && (
                <>
                  <span>{formatRuntime(movie.runtime)}</span>
                  <span className="text-slate-600">•</span>
                </>
              )}
              {movie.omdb?.rated && (
                <span>{movie.omdb.rated}</span>
              )}
            </div>

            {/* Action Buttons Row */}
            <div className="flex flex-wrap items-center gap-3 mb-8">
              {/* Play Trailer */}
              {trailerKey && (
                <button
                  onClick={() => setShowTrailer(true)}
                  className="flex items-center gap-2 h-10 px-4 rounded-lg border border-teal-500 text-teal-400 hover:bg-teal-500/10 transition-colors font-medium"
                >
                  <Play size={18} className="fill-current" />
                  Play Trailer
                </button>
              )}

              {isLoggedIn && (
                <>
                  {/* Add to Watchlist */}
                  {/* TASK A: Pass enrichment for instant metadata display on WatchlistPage */}
                  <AddToListDropdown
                    mediaId={movie.id}
                    mediaType="movie"
                    mediaTitle={movie.title}
                    mediaPoster={movie.poster_path ?? undefined}
                    mediaYear={year ? parseInt(year) : undefined}
                    enrichment={{
                      genre_ids: movie.genre_ids,
                      runtime: movie.runtime,
                      imdb_rating: movie.imdb_rating,
                      rt_rating: movie.rt_rating,
                      filmpl_rating: ourRating ?? undefined,
                      popularity: movie.popularity,
                      original_language: movie.original_language,
                      vote_count: movie.vote_count,
                    }}
                  />

                  {/* Watched Button */}
                  <button
                    onClick={toggleWatched}
                    className={`flex items-center gap-2 h-10 px-4 rounded-lg font-medium transition-all ${
                      isWatched
                        ? 'bg-teal-500 text-white hover:bg-teal-600'
                        : 'ring-1 ring-white/[0.08] text-slate-300 hover:ring-white/[0.12] hover:text-white'
                    }`}
                  >
                    <Eye size={18} />
                    {isWatched ? 'Watched' : 'Mark Watched'}
                  </button>

                  {/* Heart/Favorite Button */}
                  <button
                    onClick={handleToggleFavorites}
                    className={`flex items-center justify-center h-10 w-10 rounded-lg border transition-colors ${
                      isFavorite
                        ? 'bg-red-500/20 border-red-500 text-red-500'
                        : 'ring-1 ring-white/[0.08] text-slate-400 hover:ring-red-500 hover:text-red-500'
                    }`}
                  >
                    <Heart size={18} fill={isFavorite ? 'currentColor' : 'none'} />
                  </button>

                  {/* Rate Button */}
                  <button
                    onClick={() => setIsRatingModalOpen(true)}
                    className={`flex items-center gap-2 h-10 px-4 rounded-lg font-medium transition-all ${
                      userRating
                        ? 'bg-purple-500/20 border border-purple-500 text-purple-400'
                        : 'ring-1 ring-white/[0.08] text-slate-300 hover:ring-white/[0.12] hover:text-white'
                    }`}
                  >
                    <Star size={18} className={userRating ? 'fill-current' : ''} />
                    {userRating ? `${userRating}/10` : 'Rate'}
                  </button>

                  {/* Recommend Button */}
                  <button
                    onClick={() => setShowRecommendModal(true)}
                    className="flex items-center gap-2 h-10 px-4 rounded-lg font-medium ring-1 ring-white/[0.08] text-slate-300 hover:ring-white/[0.12] hover:text-white transition-all"
                  >
                    <Send size={18} />
                    Recommend
                  </button>
                </>
              )}

              {/* Share Button - available to everyone */}
              <ShareButton
                path={`/movie/${movie.id}`}
                title={`${movie.title}${year ? ` (${year})` : ''}`}
                image={movie.poster_path ? getPosterUrl(movie.poster_path, 'large') : undefined}
              />
            </div>

            {/* Overview */}
            <div className="mb-10">
              <h2 className="text-xl font-semibold text-white mb-3">Overview</h2>
              <p className="text-slate-300 leading-relaxed">{movie.overview}</p>
            </div>

            {/* Director & Writers */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
              {directors.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-500 uppercase mb-3">
                    {directors.length > 1 ? 'Directors' : 'Director'}
                  </h3>
                  <div className="flex flex-wrap gap-3">
                    {directors.map((director) => (
                      <Link
                        key={director.id}
                        to={`/person/${director.id}`}
                        className="flex items-center gap-3 p-2 rounded-lg bg-[#1A1F26]/50 hover:bg-[#1A1F26] transition-colors group"
                      >
                        <div className="w-12 h-12 rounded-lg overflow-hidden bg-slate-700 flex-shrink-0">
                          {director.profile_path ? (
                            <img
                              src={getProfileUrl(director.profile_path, 'small')}
                              alt={director.name}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-slate-500">
                              <span className="text-xl">👤</span>
                            </div>
                          )}
                        </div>
                        <span className="text-white group-hover:text-teal-400 transition-colors font-medium">
                          {director.name}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
              {writers.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-500 uppercase mb-3">Writers</h3>
                  <div className="flex flex-wrap gap-3">
                    {writers.map((writer, index) => (
                      <Link
                        key={`${writer.id}-${index}`}
                        to={`/person/${writer.id}`}
                        className="flex items-center gap-3 p-2 rounded-lg bg-[#1A1F26]/50 hover:bg-[#1A1F26] transition-colors group"
                      >
                        <div className="w-12 h-12 rounded-lg overflow-hidden bg-slate-700 flex-shrink-0">
                          {writer.profile_path ? (
                            <img
                              src={getProfileUrl(writer.profile_path, 'small')}
                              alt={writer.name}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-slate-500">
                              <span className="text-xl">👤</span>
                            </div>
                          )}
                        </div>
                        <span className="text-white group-hover:text-teal-400 transition-colors font-medium">
                          {writer.name}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <JustWatchWidget
          mediaType="movie"
          tmdbId={movie.id}
          imdbId={movie.imdb_id}
          title={movie.title}
          year={year ? parseInt(year, 10) : undefined}
        />

        {/* Cast Section - Horizontal Scroll */}
        {cast.length > 0 && (
          <div className="mt-12 p-6 rounded-xl border border-teal-500/30 bg-[#0F1419]/50">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <div className="w-1 h-5 bg-teal-500 rounded-full" />
                Cast
              </h2>
              <Link to="#" className="flex items-center gap-1 text-sm text-teal-400 hover:text-teal-300">
                See All <ChevronRight size={16} />
              </Link>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
              {cast.map((actor) => (
                <Link
                  key={actor.id}
                  to={`/person/${actor.id}`}
                  className="flex-shrink-0 w-20 text-center group"
                >
                  <div className="w-20 h-20 rounded-full overflow-hidden bg-[#1A1F26] mb-2 mx-auto">
                    {actor.profile_path ? (
                      <img
                        src={`https://image.tmdb.org/t/p/w185${actor.profile_path}`}
                        alt={actor.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-600">
                        <span className="text-2xl">👤</span>
                      </div>
                    )}
                  </div>
                  <h3 className="text-sm font-medium text-white group-hover:text-teal-400 transition-colors truncate">
                    {actor.name}
                  </h3>
                  <p className="text-xs text-slate-500 truncate">{actor.character}</p>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* You Might Enjoy - Similar Movies (For You shelf mechanics) */}
        <SimilarShelf
          similar={movie.similar}
          navigate={navigate}
        />

        {/* Details Card - Redesigned with teal accent */}
        <div className="mt-12 p-6 rounded-xl border border-teal-500/30 bg-[#0F1419]/50">
          <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
            <div className="w-1 h-5 bg-teal-500 rounded-full" />
            Details
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-6">
            {/* Release Date */}
            {movie.release_date && (
              <div>
                <div className="flex items-center gap-2 text-teal-400 text-xs font-semibold uppercase tracking-wider mb-1.5">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Release Date
                </div>
                <p className="text-white text-sm">
                  {new Date(movie.release_date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
            )}

            {/* Runtime */}
            {movie.runtime && (
              <div>
                <div className="flex items-center gap-2 text-teal-400 text-xs font-semibold uppercase tracking-wider mb-1.5">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Runtime
                </div>
                <p className="text-white text-sm">{formatRuntime(movie.runtime)}</p>
              </div>
            )}

            {/* Language */}
            {movie.original_language && (
              <div>
                <div className="flex items-center gap-2 text-teal-400 text-xs font-semibold uppercase tracking-wider mb-1.5">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                  </svg>
                  Language
                </div>
                <p className="text-white text-sm">
                  {(() => {
                    const langs: Record<string, string> = {
                      en: 'English', es: 'Spanish', fr: 'French', de: 'German',
                      it: 'Italian', ja: 'Japanese', ko: 'Korean', zh: 'Chinese',
                      pt: 'Portuguese', ru: 'Russian', hi: 'Hindi', ar: 'Arabic'
                    };
                    return langs[movie.original_language] || movie.original_language.toUpperCase();
                  })()}
                </p>
              </div>
            )}

            {/* Budget */}
            {movie.budget && movie.budget > 0 && (
              <div>
                <div className="flex items-center gap-2 text-teal-400 text-xs font-semibold uppercase tracking-wider mb-1.5">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Budget
                </div>
                <p className="text-white text-sm">${movie.budget.toLocaleString()}</p>
              </div>
            )}

            {/* Box Office */}
            {(movie.revenue && movie.revenue > 0) || movie.omdb?.box_office ? (
              <div>
                <div className="flex items-center gap-2 text-teal-400 text-xs font-semibold uppercase tracking-wider mb-1.5">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Box Office
                </div>
                <p className="text-white text-sm">
                  {movie.omdb?.box_office || `$${movie.revenue?.toLocaleString()}`}
                </p>
              </div>
            ) : null}

            {/* Awards */}
            {movie.omdb?.awards && movie.omdb.awards !== 'N/A' && (
              <div className="col-span-2 md:col-span-3">
                <div className="flex items-center gap-2 text-teal-400 text-xs font-semibold uppercase tracking-wider mb-1.5">
                  <Award size={14} />
                  Awards
                </div>
                <p className="text-white text-sm">{movie.omdb.awards}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Rating Modal */}
      {movie && (
        <RatingModal
          movie={{
            id: movie.id,
            title: movie.title,
            poster_path: movie.poster_path,
            backdrop_path: movie.backdrop_path,
            release_date: movie.release_date,
            media_type: 'movie',
          } as MediaItem}
          isOpen={isRatingModalOpen}
          onClose={() => setIsRatingModalOpen(false)}
        />
      )}

      {/* Recommend Modal */}
      {movie && (
        <RecommendModal
          isOpen={showRecommendModal}
          onClose={() => setShowRecommendModal(false)}
          media={{
            mediaId: movie.id,
            mediaType: 'movie',
            title: movie.title,
            poster: movie.poster_path || undefined,
            year: year ? parseInt(year) : undefined,
          }}
        />
      )}
    </div>
  );
}
